import Box from "@/ui/atoms/Box";
import {
    Dropdown,
    DropdownContent,
    DropdownSelect,
    DropdownSelectItem,
    TextField,
} from "@proda-ai/ui-kit";
import React from "react";

type TPeriod = keyof typeof Periods;

const Periods = {
    month: {
        value: "month",
        label: "Month",
        testId: "AnnualizationGroupSettings-month",
    },
    quarter: {
        value: "quarter",
        label: "Quarter",
        testId: "AnnualizationGroupSettings-quarter",
    },
    half: {
        value: "half",
        label: "Half",
        testId: "AnnualizationGroupSettings-half",
    },
    year: {
        value: "year",
        label: "Year",
        testId: "AnnualizationGroupSettings-year",
    },
};

export type AnnualizationGroupSettingsProps = JSX.IntrinsicAttributes & {
    selectedGroup: TPeriod;
    onChangeSelectedGroup: (group: TPeriod) => void;
    periodButtonsDisabled: boolean;
};

export const testIds = {
    main: "AnnualizationGroupSettings",
    annualizationGroupSettingsDropdown: "AnnualizationGroupSettingsDropdown",
    monthButton: "AnnualizationGroupSettings-month",
    quarterButton: "AnnualizationGroupSettings-quarter",
    halfButton: "AnnualizationGroupSettings-half",
    yearButton: "AnnualizationGroupSettings-year",
};

const AnnualizationGroupSettings = (props: AnnualizationGroupSettingsProps) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const [isDropdownOpen, setIsDropdownOpen] = React.useState<boolean>(false);
    const { selectedGroup, onChangeSelectedGroup, periodButtonsDisabled } =
        props;

    const onOptionSelectedFromDropdown = React.useCallback(
        (selectedItem: TPeriod) => {
            setIsDropdownOpen(false);
            onChangeSelectedGroup(selectedItem);
        },
        [onChangeSelectedGroup],
    );

    return (
        <>
            <Box data-testid={testIds.main} sx={{ width: "130px" }}>
                <Dropdown
                    isShowing={isDropdownOpen}
                    onClose={() => {
                        setIsDropdownOpen(false);
                    }}
                    isDismissible={true}
                    onItemSelected={() => {}}
                >
                    <TextField
                        ref={inputRef}
                        data-testid={testIds.annualizationGroupSettingsDropdown}
                        name={"Display columns by"}
                        label={"Display columns by"}
                        size={"tiny"}
                        disabled={periodButtonsDisabled}
                        leadingIcon={{
                            icon: "table-columns",
                            color: "accent-color",
                        }}
                        trailingIcon="caret-down"
                        value={Periods[selectedGroup].label}
                        onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                            onChangeSelectedGroup(
                                event.target.value as TPeriod,
                            );
                        }}
                        onFocus={() => {
                            setIsDropdownOpen(true);
                        }}
                        onClick={() => {
                            setIsDropdownOpen(true);
                        }}
                        onKeyDown={(event): void => {
                            if (event.key === "Escape") {
                                inputRef?.current?.blur();
                            }
                            if (event.key === "Enter") {
                                event.stopPropagation();
                            }
                        }}
                    />{" "}
                    <DropdownContent visibleItemsCount={10}>
                        <DropdownSelect
                            currentIndex={Object.values(Periods).findIndex(
                                option => option.value === selectedGroup,
                            )}
                            role="listbox"
                            labelledById={`field-${Object.values(
                                Periods,
                            ).findIndex(
                                option => option.value === selectedGroup,
                            )}`}
                        >
                            {Object.values(Periods).map((option, i) => (
                                <DropdownSelectItem
                                    data-testid={option.testId}
                                    key={`${option.value}-${i}`}
                                    role="option"
                                    onClick={(): void =>
                                        onOptionSelectedFromDropdown(
                                            option.value as TPeriod,
                                        )
                                    }
                                    label={option.label}
                                    floatedItemMinWidth={35}
                                />
                            ))}
                        </DropdownSelect>
                    </DropdownContent>
                </Dropdown>
            </Box>
        </>
    );
};

export { AnnualizationGroupSettings };
